import * as React from "react";
import { styled, Typography } from "@mui/material";
import { GraphDetailSelection } from "../util/graphDetialsSelectionHook";
import { featuresToMultiLine } from "fe-common/logic/qualifiedFeature/util";
import {
  computeSummaryInfo,
  cutPopulationFromFeatures,
  inexactProb,
  rule,
} from "../util/graphDetialUtils";
import {
  MobileView,
  SingleViewPerLayoutBreakPoint,
} from "../util/aKaunGeneralUtil";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { RangeView } from "./rangeView";

//region [[ Styles ]]

const GraphSelectionSummaryView = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  flexDirection: "row",
  flexWrap: "wrap",
  padding: "5px",
  //width: "fit-content",
  borderRadius: "10px",
  "& p": { display: "inline-block" },
  //position: "absolute",
  zIndex: 2,
  //margin: theme.spacing(2),
  [theme.breakpoints.down(SingleViewPerLayoutBreakPoint)]: {
    width: `calc(100% - ${theme.spacing(2)}`,
    boxShadow: "none",
    backgroundColor: "white",
    margin: theme.spacing(1),
    padding: "0px",
  },
}));

const RuleSource = styled("span")(({ theme }: { theme?: any }) => ({
  color: KahunColorMap.DefaultLight,
  marginBottom: "5px",
  fontSize: "14px",
  fontWeight: 500,
}));

const RuleType = styled("span")({
  fontSize: "14px",
  marginBottom: "5px",
  color: KahunColorMap.PrimaryDim,
  fontWeight: 500,
});

const RuleTarget = styled("span")(({ theme }: { theme?: any }) => ({
  color: KahunColorMap.DefaultLight,
  marginBottom: "5px",
  fontSize: "14px",
  fontWeight: 500,
}));

const CondensedTypography = styled(Typography)({
  fontFamily: "Helvetica Neue",
  marginLeft: "3px",
});

const ResourcesTitle = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  padding: 0,
  paddingLeft: "8%",
  paddingRight: "8%",
  marginTop: "10px",
  //flex: 1,
  margin: "4px",
  width: "100%",
});

const FrequencyRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
});
const FrequencyLabel = styled("span")(({ theme }: { theme?: any }) => ({
  color: KahunColorMap.FrequencyLabel,
  marginTop: "10px",
  fontSize: "0.9em",

  [theme.breakpoints.down(MobileView)]: {
    fontSize: "12px",
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface GraphSelectionSummaryProps {
  selection?: GraphDetailSelection;
  className?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
const hasMetric = (target: any) => target.metric !== 0 && target.metric !== 1;

const isMetricExact = (target: any) => hasMetric(target) && target.exact;
//endregion [[ Functions ]]

export const GraphSelectionSummary = ({
  selection,
  ...props
}: GraphSelectionSummaryProps) => {
  let { source, targets } = computeSummaryInfo(selection);
  if (!source || !targets?.length) return null;

  let sources = cutPopulationFromFeatures(source.qfs);

  return (
    <GraphSelectionSummaryView className={props.className}>
      {targets.length === 1 &&
        targets.map((t, index) => (
          <ResourcesTitle key={index}>
            <CondensedTypography>
              <RuleSource>
                {featuresToMultiLine(sources.features, ",")}
              </RuleSource>
              <RuleType>
                {t.ruletype ? rule(t.ruletype).toUpperCase() : " -> "}{" "}
              </RuleType>
              <RuleTarget>{featuresToMultiLine(t.target.qfs, ",")}</RuleTarget>
              {t.ruletype === null && " (inferred)"}{" "}
            </CondensedTypography>
            <FrequencyRow>
              <FrequencyLabel>
                {isMetricExact(t) && `${Math.round(100 * t.metric)}% of cases`}
                {isMetricExact(t) &&
                  t.min &&
                  t.max &&
                  t.min !== t.max &&
                  ` (range:${Math.round(100 * t.min)}-${Math.round(
                    100 * t.max,
                  )}%)`}
                {hasMetric(t) && !isMetricExact(t) && inexactProb(t.metric)}
              </FrequencyLabel>
            </FrequencyRow>
            {/*{isMetricExact(t) && (*/}
            {/*  <NewIndicatorSlider value={t.metric} min={t.min} max={t.max} />*/}
            {/*)}*/}
          </ResourcesTitle>
        ))}
      {targets.length > 1 && (
        <ResourcesTitle key={"single"}>
          <CondensedTypography>
            {featuresToMultiLine(sources.features, ",")} {"correlated with"}{" "}
            {targets
              .map((t) => featuresToMultiLine(t.target.qfs, "|"))
              .join(", ")}
          </CondensedTypography>
        </ResourcesTitle>
      )}
      {targets.length === 0 && (
        <ResourcesTitle key={"single"}>
          <CondensedTypography>
            {featuresToMultiLine(sources.features, ",")}
          </CondensedTypography>
        </ResourcesTitle>
      )}
    </GraphSelectionSummaryView>
  );
};

// TODO: once proven stable enough replace this with the IndicatorSlider in indicator.tsx

const formatPercent = (percent: number): number => Math.round(percent * 100);

export function NewIndicatorSlider({
  value,
  min,
  max,
}: {
  value: number;
  min: number;
  max: number;
}) {
  const valueFormatted = formatPercent(value);

  if (min >= 0 && max > min) {
    const minFormatted = formatPercent(min);
    const maxFormatted = formatPercent(max);

    return (
      <RangeView
        startRangeMark={{
          value: minFormatted - 0.0001,
          label: maxFormatted - minFormatted > 10 ? `${minFormatted}%` : "",
        }}
        endsRangeMark={{
          value: maxFormatted + 0.0001,
          label: maxFormatted - minFormatted > 10 ? `${maxFormatted}%` : "",
        }}
        mainValue={valueFormatted}
      />
    );
  } else {
    return <RangeView mainValue={valueFormatted} />;
  }
}
